import { useCurrentUser, useDialog } from '@ianesthesie-kit/redux';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TerraService } from '@/modules/terra/terra.service';
import { captureThrowOnDev } from '@/util';
import {
  useTerraIsEligibleQuery,
  useTerraIsIntegratedQuery
} from '@/modules/terra/terra.slice';
import { AppDialogType } from '@/config';
import { useTranslator } from '@ianesthesie-kit/core';
import { useCurrentHospital } from '@/modules/hospital';
import { useIsVerified } from '@/modules/userConfig';
import { useSearchParams } from 'next/navigation';

export const useIsTerraEnabledOnHospital = () => {
  const {
    queryResult: { data: hospital }
  } = useCurrentHospital();
  return !!hospital?.terraModuleEnabled;
};

export const useTerraConnect = (showResultDialog = true) => {
  const user = useCurrentUser();
  const searchParams = useSearchParams();
  const terraUserId = searchParams.get('user_id');
  const referenceId = searchParams.get('reference_id');
  const resource = searchParams.get('resource');
  const reason = searchParams.get('reason');

  const terraDisabled = !useIsTerraEnabledOnHospital();
  const isUserVerified = useIsVerified();
  const { data: isEligible = false } = useTerraIsEligibleQuery(undefined, {
    skip: terraDisabled || !isUserVerified
  });
  const { data: isIntegrated = false, refetch: refetchIsIntegrated } =
    useTerraIsIntegratedQuery(undefined, {
      skip: terraDisabled || !isUserVerified
    });
  const [status, setStatus] = useState<'loading' | 'error' | 'success'>();
  const { showDialog } = useDialog();
  const translate = useTranslator();

  const refetchInterval = useRef<ReturnType<typeof setInterval>>(undefined);

  const openWidget = useCallback(() => {
    if (!isEligible) {
      throw new Error('Not eligible to connect');
    }
    setStatus('loading');
    TerraService.getInstance()
      .generateSession()
      .then((data) => (location.href = data.url))
      .catch((err) => {
        captureThrowOnDev(err);
        setStatus('error');
      });
  }, [isEligible]);

  useEffect(() => {
    if (status === 'success' || status === 'error') {
      clearInterval(refetchInterval.current);
    }
    if (!showResultDialog || terraDisabled) {
      return;
    }
    if (status === 'success') {
      showDialog({
        dialogType: AppDialogType.SINGLE_CLOSE_BUTTON,
        title: translate('patient-app.terra.connectSuccessDialog.title'),
        message: translate('patient-app.terra.connectSuccessDialog.body'),
        severity: 'success',
        closeOnClickAway: true
      });
    } else if (status === 'error') {
      showDialog({
        dialogType: AppDialogType.SINGLE_CLOSE_BUTTON,
        title: translate('patient-app.terra.connectFailureDialog.title'),
        message: translate('patient-app.terra.connectFailureDialog.body'),
        severity: 'error',
        closeOnClickAway: true
      });
    }
  }, [showDialog, status, translate, showResultDialog, terraDisabled]);

  useEffect(() => {
    if (terraDisabled) {
      return;
    }
    if (!terraUserId && !referenceId && !resource) {
      return;
    }

    const terraResult = reason ? 'failure' : 'success';

    if (terraResult !== 'success') {
      setStatus('error');
    } else if (!terraUserId || !referenceId || !resource) {
      captureThrowOnDev(
        `Invalid terra params: ${terraUserId}, ${referenceId}, ${resource}`
      );
      setStatus('error');
    } else if (referenceId !== user?.id) {
      captureThrowOnDev(
        `Reference and user ids do not match: ${referenceId} - ${user?.id}`
      );
      setStatus('error');
    } else if (isIntegrated) {
      setStatus('success');
    } else {
      setStatus('loading');
      refetchInterval.current = setInterval(() => refetchIsIntegrated, 200);
    }

    return () => {
      if (refetchInterval.current) {
        clearInterval(refetchInterval.current);
      }
    };
  }, [
    terraDisabled,
    isIntegrated,
    reason,
    referenceId,
    refetchIsIntegrated,
    resource,
    terraUserId,
    user?.id
  ]);

  return useMemo(
    () => ({
      isEligible,
      isIntegrated,
      status,
      openWidget
    }),
    [isEligible, status, openWidget, isIntegrated]
  );
};
